import { IconChevronRight, type Icon as TablerIcon } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AnimatedSidebarActiveBackground,
  AnimatedSidebarLabel,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuItem,
  AnimatedSidebarPanel,
  AnimatedSidebarSubmenu,
  animatedSidebarSpringLayout,
  animatedSidebarSpringPress,
  getAnimatedSidebarSubmenuItemVariants,
  useAnimatedSidebar,
} from "@/components/ui/animated-sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/use-permission";
import { useShopScope } from "@/hooks/use-shop-scope";
import { useWorkspaceDataStatus } from "@/hooks/use-workspace-data-status";
import { canAccessApprovalQueue } from "@/lib/approval-access";
import { fetchApprovalProposals } from "@/lib/approval-api";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";
import {
  CampaignCreationProgress,
  type CampaignCreationProgressProps,
} from "./campaign-creation-progress";
import {
  AI_OPTIMIZATION_NAV_GROUP,
  AI_OPTIMIZATION_NAV_ITEMS,
  BUDGET_NAV_ITEM,
  DOWNLOAD_NAV_ITEM,
  EXPANSION_NAV_GROUPS,
  OVERVIEW_NAV_ITEM,
  STRATEGY_LAB_NAV_GROUP,
  STRATEGY_LAB_VIEWS,
} from "./navigation";
import { WorkspaceSelect } from "./workspace-select";

const navItemClassName =
  "relative flex w-full items-center gap-2 overflow-hidden rounded-sm px-2 text-sm text-text-secondary outline-none transition-colors duration-150 ease-out hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring";
const subItemClassName =
  "flex h-[30px] items-center gap-2 rounded-sm px-2 text-sm text-text-secondary outline-none transition-colors duration-150 ease-out hover:bg-state-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring aria-[current=page]:bg-sidebar-active aria-[current=page]:font-medium aria-[current=page]:text-ink";

interface AppSidebarProps {
  creationProgress?: Omit<CampaignCreationProgressProps, "collapsed"> & { identity: string | null };
}

export function AppSidebar({ creationProgress }: AppSidebarProps): React.ReactElement {
  const { t } = useTranslation("layout");
  const location = useLocation();
  const { expanded, setExpanded } = useAnimatedSidebar();
  const activeWorkspaceId = useAuthStore((state) => state.user?.activeWorkspaceId);
  const shops = useAuthStore((state) => state.shops);
  // ADP-127: 角标 = 当前店铺范围内的待审批数量。聚合视角下跨店统计,与
  // 竞价审批页的待审批分区同源(同一 shopIds 参数),数量天然一致。
  const { scopeShopIds } = useShopScope();
  const { hasModule } = usePermission();
  const aiNavItems = AI_OPTIMIZATION_NAV_ITEMS.filter((item) => hasModule(item.module));
  const canReview =
    hasModule("approvals") && scopeShopIds.some((shopId) => canAccessApprovalQueue(shops, shopId));
  const syncQuery = useWorkspaceDataStatus(activeWorkspaceId);
  const isAiOptimizationRoute = AI_OPTIMIZATION_NAV_ITEMS.some((item) =>
    location.pathname.startsWith(item.to),
  );
  const isStrategyLabRoute = location.pathname.startsWith(STRATEGY_LAB_NAV_GROUP.to);
  const strategyLabView = (location.search as { view?: string }).view ?? "library";
  const [openNavGroups, setOpenNavGroups] = useState<string[]>(() =>
    [
      isAiOptimizationRoute ? AI_OPTIMIZATION_NAV_GROUP.value : null,
      isStrategyLabRoute ? STRATEGY_LAB_NAV_GROUP.value : null,
    ].filter((value): value is string => value !== null),
  );
  const approvalQuery = useQuery({
    queryKey: ["approvals", "sidebar-count", scopeShopIds],
    queryFn: () =>
      fetchApprovalProposals({
        ...DEFAULT_APPROVAL_SEARCH,
        pageSize: 1,
        shopIds: scopeShopIds.join(","),
      }),
    enabled: canReview && scopeShopIds.length > 0,
    staleTime: 30_000,
  });
  const pendingApprovals = approvalQuery.data?.summary.pending ?? 0;

  useEffect(() => {
    if (isAiOptimizationRoute)
      setOpenNavGroups((groups) => withGroup(groups, AI_OPTIMIZATION_NAV_GROUP.value));
  }, [isAiOptimizationRoute]);

  useEffect(() => {
    if (isStrategyLabRoute)
      setOpenNavGroups((groups) => withGroup(groups, STRATEGY_LAB_NAV_GROUP.value));
  }, [isStrategyLabRoute]);

  function toggleGroup(value: string): void {
    if (!expanded) {
      setExpanded(true);
      setOpenNavGroups([value]);
      return;
    }
    setOpenNavGroups((groups) =>
      groups.includes(value) ? groups.filter((group) => group !== value) : [...groups, value],
    );
  }

  return (
    <AnimatedSidebarPanel className="flex h-full flex-col overflow-hidden border-r border-line bg-sidebar px-3 pt-1.5 pb-3">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex h-[52px] shrink-0 items-center">
          <WorkspaceSelect collapsed={!expanded} />
        </div>

        <nav aria-label={t("nav.ariaLabel")} className="flex min-h-0 flex-1 flex-col">
          <div className="flex h-[18px] shrink-0 items-start px-2">
            <AnimatedSidebarLabel className="min-w-0 flex-1 truncate text-badge font-semibold tracking-wide text-muted-foreground">
              {t("nav.sectionLabel")}
            </AnimatedSidebarLabel>
          </div>
          <div className="paper-scrollbar -mx-3 min-h-0 flex-1 overflow-y-auto px-3">
            <AnimatedSidebarMenu className="gap-0.5">
              {hasModule(OVERVIEW_NAV_ITEM.module) ? (
                <AnimatedSidebarMenuItem key={OVERVIEW_NAV_ITEM.to}>
                  <NavigationItem
                    item={OVERVIEW_NAV_ITEM}
                    active={location.pathname === "/"}
                    label={t(OVERVIEW_NAV_ITEM.labelKey)}
                    collapsed={!expanded}
                  />
                </AnimatedSidebarMenuItem>
              ) : null}
              {aiNavItems.length > 0 ? (
                <AnimatedSidebarMenuItem key={AI_OPTIMIZATION_NAV_GROUP.value}>
                  <SidebarNavigationGroup
                    label={t(AI_OPTIMIZATION_NAV_GROUP.labelKey)}
                    icon={AI_OPTIMIZATION_NAV_GROUP.icon}
                    active={isAiOptimizationRoute}
                    expanded={openNavGroups.includes(AI_OPTIMIZATION_NAV_GROUP.value)}
                    pending={pendingApprovals}
                    onToggle={() => toggleGroup(AI_OPTIMIZATION_NAV_GROUP.value)}
                  >
                    {aiNavItems.map((item) => (
                      <SubmenuItem key={item.to}>
                        <Link
                          to={item.to}
                          search={
                            item.to === "/ai-operations" ? DEFAULT_APPROVAL_SEARCH : undefined
                          }
                          aria-current={location.pathname.startsWith(item.to) ? "page" : undefined}
                          className={subItemClassName}
                        >
                          <span className="min-w-0 flex-1 truncate">{t(item.labelKey)}</span>
                          {item.to === "/ai-operations" && pendingApprovals ? (
                            <NavCount count={pendingApprovals} />
                          ) : null}
                        </Link>
                      </SubmenuItem>
                    ))}
                  </SidebarNavigationGroup>
                </AnimatedSidebarMenuItem>
              ) : null}
              {hasModule(STRATEGY_LAB_NAV_GROUP.module) ? (
                <AnimatedSidebarMenuItem key={STRATEGY_LAB_NAV_GROUP.value}>
                  <SidebarNavigationGroup
                    label={t(STRATEGY_LAB_NAV_GROUP.labelKey)}
                    icon={STRATEGY_LAB_NAV_GROUP.icon}
                    active={isStrategyLabRoute}
                    expanded={openNavGroups.includes(STRATEGY_LAB_NAV_GROUP.value)}
                    onToggle={() => toggleGroup(STRATEGY_LAB_NAV_GROUP.value)}
                  >
                    {STRATEGY_LAB_VIEWS.map((view) => (
                      <SubmenuItem key={view.value}>
                        <Link
                          to="/strategy-lab"
                          search={{ view: view.value }}
                          aria-current={
                            isStrategyLabRoute && strategyLabView === view.value
                              ? "page"
                              : undefined
                          }
                          className={subItemClassName}
                        >
                          <span className="truncate">{t(view.labelKey)}</span>
                        </Link>
                      </SubmenuItem>
                    ))}
                  </SidebarNavigationGroup>
                </AnimatedSidebarMenuItem>
              ) : null}
              {EXPANSION_NAV_GROUPS.map((group) => (
                <AnimatedSidebarMenuItem key={group.value}>
                  <SidebarPlaceholderGroup group={group} />
                </AnimatedSidebarMenuItem>
              ))}
              {hasModule(BUDGET_NAV_ITEM.module) ? (
                <AnimatedSidebarMenuItem key={BUDGET_NAV_ITEM.to}>
                  <NavigationItem
                    item={BUDGET_NAV_ITEM}
                    active={location.pathname.startsWith(BUDGET_NAV_ITEM.to)}
                    label={t(BUDGET_NAV_ITEM.labelKey)}
                    collapsed={!expanded}
                  />
                </AnimatedSidebarMenuItem>
              ) : null}
              <AnimatedSidebarMenuItem key="download-center">
                <NavigationItem
                  item={DOWNLOAD_NAV_ITEM}
                  active={location.pathname === "/coming-soon/download-center"}
                  label={t(DOWNLOAD_NAV_ITEM.labelKey)}
                  collapsed={!expanded}
                />
              </AnimatedSidebarMenuItem>
            </AnimatedSidebarMenu>
          </div>
        </nav>
      </div>

      <div className="flex shrink-0 flex-col gap-2 pt-2">
        {creationProgress && (
          <CampaignCreationProgress
            key={creationProgress.identity}
            tasks={creationProgress.tasks}
            reconnecting={creationProgress.reconnecting}
            hasMore={creationProgress.hasMore}
            collapsed={!expanded}
          />
        )}
        <WorkspaceDataNotice
          status={syncQuery.data}
          unavailable={syncQuery.isError}
          collapsed={!expanded}
          canManage={hasModule("settings")}
        />
      </div>
    </AnimatedSidebarPanel>
  );
}

function withGroup(groups: string[], value: string): string[] {
  return groups.includes(value) ? groups : [...groups, value];
}

function SubmenuItem({ children }: { children: React.ReactNode }): React.ReactElement {
  const { reduceMotion } = useAnimatedSidebar();
  return (
    <motion.li
      variants={getAnimatedSidebarSubmenuItemVariants(reduceMotion)}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={animatedSidebarSpringPress}
    >
      {children}
    </motion.li>
  );
}

function SidebarPlaceholderGroup({
  group,
}: {
  group: (typeof EXPANSION_NAV_GROUPS)[number];
}): React.ReactElement {
  const { t } = useTranslation("layout");
  const { pathname } = useLocation();
  const { expanded: sidebarExpanded, setExpanded: setSidebarExpanded } = useAnimatedSidebar();
  const [open, setOpen] = useState(false);

  return (
    <SidebarNavigationGroup
      label={t(group.labelKey)}
      icon={group.icon}
      active={group.items.some((item) =>
        "to" in item ? pathname.startsWith(item.to) : pathname === `/coming-soon/${item.feature}`,
      )}
      expanded={open}
      onToggle={() => {
        if (!sidebarExpanded) {
          setSidebarExpanded(true);
          setOpen(true);
          return;
        }
        setOpen((current) => !current);
      }}
    >
      {group.items.map((item) => (
        <SubmenuItem key={item.labelKey}>
          {"to" in item ? (
            <Link
              to={item.to}
              aria-current={pathname.startsWith(item.to) ? "page" : undefined}
              className={subItemClassName}
            >
              <span className="truncate">{t(item.labelKey)}</span>
            </Link>
          ) : (
            <Link
              to="/coming-soon/$feature"
              params={{ feature: item.feature }}
              aria-current={pathname === `/coming-soon/${item.feature}` ? "page" : undefined}
              className={subItemClassName}
            >
              <span className="truncate">{t(item.labelKey)}</span>
            </Link>
          )}
        </SubmenuItem>
      ))}
    </SidebarNavigationGroup>
  );
}

function SidebarNavigationGroup({
  active,
  children,
  expanded,
  icon: Icon,
  label,
  onToggle,
  pending = 0,
}: {
  active: boolean;
  children: React.ReactNode;
  expanded: boolean;
  icon: TablerIcon;
  label: string;
  onToggle: () => void;
  pending?: number;
}): React.ReactElement {
  const { expanded: sidebarExpanded, reduceMotion } = useAnimatedSidebar();
  // The group header carries the pending count only while its list is closed.
  const showPending = sidebarExpanded && !expanded && pending > 0;
  const trigger = (
    <motion.button
      type="button"
      aria-label={sidebarExpanded ? undefined : label}
      aria-expanded={sidebarExpanded ? expanded : undefined}
      onClick={onToggle}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={animatedSidebarSpringPress}
      className={cn(
        navItemClassName,
        "h-8 font-medium",
        !sidebarExpanded && "justify-center px-0",
        active && "text-ink",
      )}
    >
      <span className="relative z-10 flex size-4 shrink-0 items-center justify-center">
        <Icon className="size-4" stroke={1.75} aria-hidden />
      </span>
      <AnimatedSidebarLabel className="relative z-10 min-w-0 flex-1 truncate text-left">
        {label}
      </AnimatedSidebarLabel>
      {showPending ? <NavCount count={pending} /> : null}
      <AnimatedSidebarLabel className="relative z-10 flex size-4 shrink-0 items-center justify-center text-muted-foreground">
        <motion.span
          animate={{ rotate: expanded ? 90 : 0 }}
          initial={false}
          transition={reduceMotion ? { duration: 0 } : animatedSidebarSpringLayout}
          className="flex size-3.5 items-center justify-center"
        >
          <IconChevronRight className="size-3.5" stroke={2} aria-hidden />
        </motion.span>
      </AnimatedSidebarLabel>
    </motion.button>
  );

  return (
    <div>
      {sidebarExpanded ? (
        trigger
      ) : (
        <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      )}
      <AnimatedSidebarSubmenu open={expanded}>
        <div className="pt-0.5 pl-[15px]">
          <ul className="flex flex-col gap-0.5 border-l border-line pl-3">{children}</ul>
        </div>
      </AnimatedSidebarSubmenu>
    </div>
  );
}

function NavigationItem({
  item,
  active,
  label,
  collapsed = false,
}: {
  item: typeof OVERVIEW_NAV_ITEM | typeof BUDGET_NAV_ITEM | typeof DOWNLOAD_NAV_ITEM;
  active: boolean;
  label: string;
  collapsed?: boolean;
}): React.ReactElement {
  const { reduceMotion } = useAnimatedSidebar();
  const Icon = item.icon;

  const link = (
    <Link
      to={item.to}
      params={"params" in item ? item.params : undefined}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      className={cn(
        navItemClassName,
        "h-8 font-medium",
        collapsed && "justify-center px-0",
        active && "font-semibold text-ink",
      )}
    >
      <AnimatedSidebarActiveBackground active={active} className="rounded-sm" />
      <span className="relative z-10 flex size-4 shrink-0 items-center justify-center">
        <Icon className="size-4" stroke={1.75} aria-hidden />
      </span>
      {!collapsed ? <span className="relative z-10 min-w-0 flex-1 truncate">{label}</span> : null}
    </Link>
  );

  return (
    <motion.div
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={animatedSidebarSpringPress}
      className="w-full"
    >
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger render={link} />
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </motion.div>
  );
}

function NavCount({ count }: { count: number }): React.ReactElement {
  return (
    <span className="relative z-10 flex h-[18px] min-w-5 shrink-0 items-center justify-center rounded-full bg-warning-background px-1.5 text-micro font-semibold text-warning-foreground tabular-nums">
      {count}
    </span>
  );
}

const statusDotClassName = {
  ready: "bg-success ring-success/18",
  updating: "bg-success ring-success/18",
  preparing: "bg-info ring-info/18",
  delayed: "bg-warning ring-warning/18",
  action_required: "bg-warning ring-warning/18",
  // Reports not fully verified yet: main flags it as a warning alongside "delayed".
  unverified: "bg-warning ring-warning/18",
  paused: "bg-placeholder ring-placeholder/18",
  unavailable: "bg-placeholder ring-placeholder/18",
} as const;

export function WorkspaceDataNotice({
  status,
  unavailable,
  collapsed,
  canManage,
}: {
  status: ReturnType<typeof useWorkspaceDataStatus>["data"];
  unavailable: boolean;
  collapsed: boolean;
  canManage: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation("settings");
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const state = unavailable ? "unavailable" : status?.state;
  const hasUsableData = status?.shops.some((shop) =>
    shop.sources.some(
      (source) =>
        (source.source === "catalog" || source.source === "ads") &&
        source.hasData &&
        source.state !== "paused",
    ),
  );
  const firstPreparation = state === "preparing" && !hasUsableData;
  const attention = state === "action_required";
  const visualState = !state
    ? "unavailable"
    : state === "preparing" && hasUsableData
      ? "updating"
      : state;
  const label = !state ? t("dataStatus.loading") : t(`dataStatus.summary.${visualState}`);
  const lastSyncedAt = latestSuccess(status);
  const shortLabel = !state
    ? t("dataStatus.loading")
    : visualState === "ready" && lastSyncedAt
      ? t("dataStatus.short.syncedAt", { time: formatClock(lastSyncedAt, timeZone) })
      : t(`dataStatus.short.${visualState}`);
  const title = t("dataStatus.sidebarLabel");
  const accessibleLabel = `${title}：${label}`;
  const dot = (
    <span
      aria-hidden
      className={cn(
        "size-1.5 shrink-0 rounded-full ring-3",
        statusDotClassName[visualState],
        firstPreparation && "animate-pulse",
      )}
    />
  );
  const className = cn(
    "flex rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus-ring",
    collapsed ? "size-8 items-center justify-center" : "w-full flex-col gap-1 px-2 py-1.5",
    canManage && "hover:bg-state-hover",
  );
  const content = collapsed ? (
    dot
  ) : (
    <>
      <span className="flex h-4 w-full items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2.5">
          {dot}
          <span className="truncate text-xs font-medium text-text-secondary">
            {t("dataStatus.sidebarSource")}
          </span>
        </span>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{shortLabel}</span>
      </span>
      {attention || firstPreparation ? (
        <span className="pl-4 text-left text-xs text-text-secondary">
          {label}
          {attention && !canManage ? (
            <span className="block text-muted-foreground">{t("dataStatus.readOnly")}</span>
          ) : null}
        </span>
      ) : null}
    </>
  );
  const entry = canManage ? (
    <Link
      to="/settings"
      search={{ tab: "amazon", dataDetails: true }}
      className={className}
      aria-label={accessibleLabel}
    >
      {content}
    </Link>
  ) : (
    <button
      type="button"
      aria-disabled="true"
      className={className}
      aria-label={attention ? `${accessibleLabel}。 ${t("dataStatus.readOnly")}` : accessibleLabel}
    >
      {content}
    </button>
  );
  return (
    <Tooltip>
      <TooltipTrigger render={entry} />
      <TooltipContent side={collapsed ? "right" : "top"}>
        {accessibleLabel}
        {!canManage ? <p>{t("dataStatus.readOnly")}</p> : null}
      </TooltipContent>
    </Tooltip>
  );
}

function latestSuccess(status: ReturnType<typeof useWorkspaceDataStatus>["data"]): string | null {
  let latest: string | null = null;
  for (const shop of status?.shops ?? []) {
    for (const source of shop.sources) {
      if (source.lastSuccessAt && (!latest || source.lastSuccessAt > latest)) {
        latest = source.lastSuccessAt;
      }
    }
  }
  return latest;
}

function formatClock(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" }).format(
      new Date(iso),
    );
  }
}

type CollapsedSyncVisualState =
  | "connected"
  | "syncing"
  | "attention"
  | "disconnected"
  | "unavailable";

export function getCollapsedSyncVisualState(
  state:
    | "idle"
    | "healthy"
    | "syncing"
    | "failed"
    | "reconnect_required"
    | "disconnected"
    | undefined,
  unavailable: boolean,
): CollapsedSyncVisualState {
  if (unavailable || state === undefined) return "unavailable";
  if (state === "syncing") return "syncing";
  if (state === "failed" || state === "reconnect_required") return "attention";
  if (state === "disconnected") return "disconnected";
  return "connected";
}
